/**
 * Who else is on a document right now, filtered to connected assistants.
 *
 * Awareness is the same channel that carries device cursors in the editor;
 * the sync server relays it between every client of a doc without storing
 * anything. An assistant (Ask Claude, or any model over MCP or REST) sets
 * `user.kind = "agent"` with the name it introduced itself with, and this
 * hook surfaces those states so the Tasks, Cards and Notes surfaces can show
 * a "Claude is here" chip while it works. Presence is never persisted.
 */
import { useEffect, useState } from "react";
import type { PresenceUser } from "@hydra-notes/shared";
import { ctxFor } from "./store";

export interface AgentPresence extends PresenceUser {
  clientId: number;
  /** True while the assistant has a caret placed in this document. */
  writing: boolean;
}

interface AwarenessLike {
  clientID: number;
  getStates(): Map<number, Record<string, unknown>>;
  on(event: "change", cb: () => void): void;
  off(event: "change", cb: () => void): void;
}

const EMPTY: AgentPresence[] = [];

function collect(aw: AwarenessLike): AgentPresence[] {
  const out: AgentPresence[] = [];
  const seen = new Set<string>();
  for (const [clientId, state] of aw.getStates()) {
    if (clientId === aw.clientID) continue;
    const user = state.user as PresenceUser | undefined;
    if (!user || user.kind !== "agent" || !user.name) continue;
    if (seen.has(user.name)) continue;
    seen.add(user.name);
    out.push({ ...user, clientId, writing: state.cursor != null });
  }
  return out;
}

/**
 * Assistants present on one document. `docName` is the sync doc name; the
 * space id picks the mounted context (personal or shared). The doc must be
 * attached by someone else in this tab (an open editor, a mounted list) for
 * awareness to exist; this hook polls briefly until it does.
 */
export function useDocPresence(spaceId: string | undefined, docName: string | undefined): AgentPresence[] {
  const [present, setPresent] = useState<AgentPresence[]>(EMPTY);

  useEffect(() => {
    if (!spaceId || !docName) return;
    let aw: AwarenessLike | null = null;
    let disposed = false;
    const onChange = () => {
      if (aw) setPresent(collect(aw));
    };
    const tryAttach = () => {
      if (disposed || aw) return;
      const found = ctxFor(spaceId)?.sync?.getAwareness(docName) as AwarenessLike | null;
      if (!found) return;
      aw = found;
      aw.on("change", onChange);
      onChange();
    };
    tryAttach();
    const poll = setInterval(() => {
      if (aw) {
        // a re-attached provider (reconnect) replaces the awareness instance
        const current = ctxFor(spaceId)?.sync?.getAwareness(docName) as AwarenessLike | null;
        if (current && current !== aw) {
          aw.off("change", onChange);
          aw = null;
          setPresent(EMPTY);
        }
      }
      tryAttach();
    }, 1000);
    return () => {
      disposed = true;
      clearInterval(poll);
      aw?.off("change", onChange);
      setPresent(EMPTY);
    };
  }, [spaceId, docName]);

  return present;
}
